/**
 * Google Analytics 4 Utilities
 *
 * Helpers for tracking events in GA4 (page views are gtag-owned — see public/index.html).
 * Measurement ID: G-3FD6JEMK2D
 *
 * @module utils/analytics
 */

const APP_STORE_ID = '6754615691';
const APPLE_PT = '128376572'; // App Store Connect provider token — enables ct campaign attribution

/**
 * Build the App Store URL for a CTA surface, with Apple campaign attribution.
 * If the visitor arrived from a tagged ad (utm_campaign), `ct` carries that campaign so the
 * download attributes to it in App Store Connect; otherwise `ct` is the web surface.
 *
 * @param {string} surface - "header" | "bottom" | "home-act-card"
 */
export const appStoreUrl = (surface) => {
  let ct = 'web-' + surface;
  try {
    const c = new URLSearchParams(window.location.search).get('utm_campaign');
    if (c) ct = 'fb-' + c.toLowerCase().replace(/[^a-z0-9-]+/g, '-').slice(0, 40);
  } catch (e) { /* SSR / no window */ }
  return `https://apps.apple.com/app/cuesesh/id${APP_STORE_ID}?pt=${APPLE_PT}&ct=${encodeURIComponent(ct)}&mt=8`;
};

/**
 * Track a custom event in Google Analytics 4
 *
 * @param {string} eventName - Name of the event (e.g., "waitlist_signup")
 * @param {Object} eventParams - Additional event parameters
 * @param {string} [eventParams.event_category] - Event category
 * @param {string} [eventParams.event_label] - Event label
 * @param {number} [eventParams.value] - Event value
 *
 * @example
 * trackEvent('waitlist_signup', {
 *   event_category: 'conversion',
 *   event_label: 'Landing Page Waitlist',
 *   value: 1
 * });
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track button/CTA clicks
 *
 * @param {string} buttonText - Text of the button clicked
 * @param {string} location - Where the button is located (e.g., "hero", "footer")
 */
export const trackButtonClick = (buttonText, location) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: buttonText,
    location: location
  });
};

/**
 * Track external link clicks
 *
 * @param {string} url - The external URL clicked
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (url, linkText) => {
  trackEvent('external_link_click', {
    event_category: 'engagement',
    event_label: linkText,
    url: url
  });
};

/**
 * Track an App Store CTA click — THE single primary web conversion (every surface that
 * sends a user to the App Store fires this one event, segmented by `location`).
 * GA4 `app_store_click{location}` (location is a registered custom dimension) + Meta Pixel
 * STANDARD `Lead` (so the optimizer can bid toward it). Guarded — fbq is a no-op until the
 * pixel base + consent load.
 *
 * @param {string} location - Which surface: "header" | "bottom" | "home_act_card" (also in
 *   `public/try.html`: "try_card" | "try_badge")
 * @param {Object} [extra] - Registered-dimension params some surfaces carry (show, position, minutes)
 */
export const trackAppStoreClick = (location, extra = {}) => {
  trackEvent('app_store_click', { location, ...extra });
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Lead', { location, ...extra }); // same keys GA uses — one param set across both systems
  }
};

/**
 * Track social media link clicks
 *
 * @param {string} platform - Social platform (e.g., "Twitter", "Instagram")
 */
export const trackSocialClick = (platform) => {
  trackEvent('social_click', {
    event_category: 'engagement',
    event_label: platform
  });
};

/**
 * Track waitlist signup conversion
 * This is the main conversion event for CueSesh
 *
 * @param {string} source - Where the signup occurred (e.g., "landing_hero", "landing_bottom")
 */
export const trackWaitlistSignup = (source = 'unknown') => {
  trackEvent('waitlist_signup', {
    event_category: 'conversion',
    event_label: source,
    value: 1
  });
};

/**
 * Track form errors/abandonment
 *
 * @param {string} formName - Name of the form
 * @param {string} errorType - Type of error (e.g., "validation", "submission")
 */
export const trackFormError = (formName, errorType) => {
  trackEvent('form_error', {
    event_category: 'form',
    event_label: formName,
    error_type: errorType
  });
};
